import type { Signal } from '@preact/signals-core'

/**
 * A device's role in a video session, derived from how many mates map to its
 * clientId (see @merels/video/session's makeVideoSession docs for the mapping).
 * - solo: exactly one mate on this device -> publishes camera + mic.
 * - shared: two or more mates on this device (shared-screen) -> publishes mic only,
 *   since one camera can't represent multiple distinct mates.
 * - viewer: no mates on this device (e.g. a TV/display client) -> subscribe-only,
 *   publishes nothing.
 */
export type VideoRole = 'solo' | 'shared' | 'viewer'

export interface VideoParticipant {
  /** Merels clientId this participant maps to (see identity mapping in session.ts). */
  clientId: string
  hasVideo: boolean
  hasAudio: boolean
  videoTrack?: MediaStreamTrack
  audioTrack?: MediaStreamTrack
  isLocal: boolean
}

export type VideoConnectionState = 'idle' | 'joining' | 'joined' | 'error' | 'left'

export interface VideoSessionConfig {
  /** Daily room URL to join. */
  roomUrl: string
  /** Daily meeting token issued by the worker for this (room, clientId) pair. */
  token: string
  role: VideoRole
  /** Merels clientId for the local device joining this session. */
  clientId: string
}

export interface VideoLocalState {
  cameraOn: boolean
  micOn: boolean
}

export interface MediaDeviceOption {
  deviceId: string
  /** Human-readable device label; browsers only populate this once permission is granted. */
  label: string
}

export interface VideoDeviceState {
  cameras: MediaDeviceOption[]
  mics: MediaDeviceOption[]
  selectedCameraId: string | null
  selectedMicId: string | null
}

/** Shared "nothing selected yet" value - reused by both the real and fake sessions. */
export const EMPTY_DEVICE_STATE: VideoDeviceState = { cameras: [], mics: [], selectedCameraId: null, selectedMicId: null }

export interface VideoSession {
  join(): Promise<void>
  leave(): Promise<void>
  /** Participants keyed by Merels clientId (not Daily's internal session id). */
  participants: Signal<Map<string, VideoParticipant>>
  localState: Signal<VideoLocalState>
  connectionState: Signal<VideoConnectionState>
  /** Available/selected camera and mic, refreshed on join and on device changes. */
  devices: Signal<VideoDeviceState>
  /** No-op for the 'viewer' role, which never publishes local tracks. */
  setCameraEnabled(enabled: boolean): void
  /** No-op for the 'viewer' role, which never publishes local tracks. */
  setMicEnabled(enabled: boolean): void
  /** No-op for the 'viewer' role, which never publishes local tracks. */
  setCameraDevice(deviceId: string): void
  /** No-op for the 'viewer' role, which never publishes local tracks. */
  setMicDevice(deviceId: string): void
}
